import React, { useState, useCallback } from 'react';

const CASE_TYPE_COLORS = {
  'lite-agent': '#3AAFA9',
  'studio-agent': '#4CAF50',
  'workflow': '#D4A843',
  'app': '#7B68EE',
  'analytics': '#FF7043',
  'custom': '#999',
  'manual': '#999',
};

// Same suggestion → same colour. The model's caseType can vary (or be missing),
// so resolve the colour by the VISIBLE caseTypeLabel first, then caseType, then
// a keyword fallback — guaranteeing e.g. "Power Automate Workflow" is always the
// same colour and "Manuelle Aufgabe" is always grey.
const CASE_LABEL_COLORS = {
  'Lite Agent': '#3AAFA9',
  'Copilot Studio Agent': '#4CAF50',
  'Power Automate Workflow': '#D4A843',
  'Power App': '#7B68EE',
  'Fabric Analytics': '#FF7043',
  'Custom Development': '#999',
  'Manuelle Aufgabe': '#999',
};

function resolveCaseColor(caseType, caseTypeLabel) {
  const label = (caseTypeLabel || '').trim();
  if (CASE_LABEL_COLORS[label]) return CASE_LABEL_COLORS[label];
  if (caseType && CASE_TYPE_COLORS[caseType]) return CASE_TYPE_COLORS[caseType];
  const l = label.toLowerCase();
  if (l.includes('lite agent')) return '#3AAFA9';
  if (l.includes('power automate') || l.includes('workflow')) return '#D4A843';
  if (l.includes('studio')) return '#4CAF50';
  if (l.includes('power app')) return '#7B68EE';
  if (l.includes('fabric') || l.includes('analytics')) return '#FF7043';
  if (l.includes('manuell') || l.includes('manual')) return '#999';
  if (l.includes('custom') || l.includes('eigenentwicklung')) return '#999';
  return '#666';
}

export default function ImplementPanel({
  step, implementation, isLoading, error, onRetry,
  onClose, mappingData, processName,
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const toggleItem = useCallback((index) => {
    setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  // Empty state
  if (!step) {
    return (
      <div className="implement-panel empty">
        <div className="panel-empty-state">
          <div className="empty-title">{processName || 'Prozess'}</div>
          <h3>Implementierungsdetails</h3>
          <p>Klicke auf einen Prozessschritt, um Implementierungs-Details zu sehen.</p>
        </div>
      </div>
    );
  }

  const impl = implementation;

  // Determine case type info
  const caseTypeLabel = impl?.caseTypeLabel || step.caseTypeLabel || null;
  const caseType = impl?.caseType || step.caseType || null;
  const caseColor = resolveCaseColor(caseType, caseTypeLabel);
  // Topic 10: show the tool name without the "M365" prefix — covers both fresh
  // prompt output and any cached/older values that still carry the prefix.
  const toolName = (impl?.toolDisplayName || step.toolDisplayName || step.toolRecommendation || '').replace(/^M365\s+/, '');

  return (
    <div className="implement-panel">
      <div className="panel-header">
        <div>
          {step.isLiteAgent && step.liteAgentRank && (
            <span className="panel-lite-agent-label">
              {step.liteAgentSuggested ? 'Empfohlener Lite Agent' : 'Lite Agent'} #{step.liteAgentRank}
            </span>
          )}
          <h2>{step.name}</h2>
          <p className="panel-subtitle">{step.description}</p>
        </div>
        <button className="panel-close" onClick={onClose}>&times;</button>
      </div>

      {/* Case Type Badge */}
      {caseTypeLabel && (
        <div className="case-type-badge" style={{ borderColor: caseColor, color: caseColor }}>
          <span className="case-type-dot" style={{ backgroundColor: caseColor }} />
          Empfohlene Umsetzung: {caseTypeLabel}
        </div>
      )}
      {(() => {
        const lvl = impl?.level || step.level || null;
        return lvl && lvl !== 'L2' ? (
          <div
            className="not-mvp-badge"
            title="Diese Empfehlung kannst du im AI-Day Playground heute nicht selbst bauen. Sie ist eine Option für die spätere Umsetzung in deinem Tagesgeschäft."
            style={{ background: '#f5f5f5', border: '1px solid #ccc', color: '#888', padding: '3px 8px', fontSize: '0.72rem', borderRadius: '4px', marginTop: '4px', marginBottom: '10px', display: 'inline-block' }}
          >
            Noch nicht verfügbar (nicht MVP)
          </div>
        ) : null;
      })()}

      {/* Capability Card */}
      {step.primaryCapability && (
        <div className="capability-card">
          <span className="capability-class">{step.primaryCapability.class}</span>
          <span className="capability-app">{step.primaryCapability.application}</span>
        </div>
      )}

      {/* Tool Recommendation */}
      {(step.toolDisplayName || step.toolRecommendation) && (
        <div className="tool-recommendation">
          <div className="tool-header">
            <span className="tool-name">{toolName}</span>
          </div>
          {caseType === 'lite-agent' && (
            <p className="tool-subtext">Hier baust du deinen Agenten direkt in Microsoft Copilot.</p>
          )}
          {(step.toolDescription || step.toolReason) && (
            <p className="tool-reason">{step.toolDescription || step.toolReason}</p>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="impl-loading">
          <div className="spinner-small" />
          <span>Implementierungsdetails werden generiert...</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="impl-loading">
          <div className="error-message">{error}</div>
          {onRetry && (
            <button className="btn btn-generate" onClick={onRetry} style={{ marginTop: '0.75rem' }}>
              Erneut versuchen
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && impl && (
        <>
          {/* Instruction Prompt — moved up, directly after tool recommendation */}
          {impl.instructionPrompt && caseType === 'lite-agent' && (
            <div className="instruction-section">
              <button className="prompt-toggle" onClick={() => setShowPrompt(!showPrompt)}>
                {showPrompt ? '▼' : '▶'} Anweisungs-Prompt für den Agent Builder
              </button>
              {showPrompt && (
                <div className="prompt-container">
                  <pre className="prompt-text">{impl.instructionPrompt}</pre>
                  <button className="btn btn-copy" onClick={() => handleCopy(impl.instructionPrompt)}>
                    {copied ? 'Kopiert!' : 'In Zwischenablage kopieren'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Action Items -- Expandable */}
          <div className="action-items">
            <h3>Schritte zur Umsetzung</h3>
            <div className="action-item-list">
              {impl.actionItems?.map((item, i) => {
                const isObject = typeof item === 'object';
                const title = isObject ? item.title : `Schritt ${i + 1}`;
                const summary = isObject ? item.summary : item;
                const details = isObject ? item.details : null;
                const isExpanded = expandedItems[i];

                return (
                  <div key={i} className={`action-item-card ${isExpanded ? 'expanded' : ''}`}>
                    <div className="action-item-header">
                      <div className="action-item-number">{i + 1}</div>
                      <div className="action-item-text">
                        <span className="action-item-title">{title}</span>
                        <span className="action-item-summary">{summary}</span>
                      </div>
                      {details && (
                        <button className="action-item-toggle" onClick={() => toggleItem(i)}>
                          {isExpanded ? 'Einklappen' : 'Details anzeigen'}
                        </button>
                      )}
                    </div>
                    {isExpanded && details && (
                      <div className="action-item-details">
                        {details}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Data Sources Needed */}
          {impl.dataSourcesNeeded?.length > 0 && (
            <div className="data-sources">
              <h3>Benötigte Datenquellen</h3>
              <div className="data-source-list">
                {impl.dataSourcesNeeded.map((ds, i) => {
                  const name = typeof ds === 'string' ? ds : ds.name;
                  const type = typeof ds === 'object' ? ds.type : null;
                  const critical = typeof ds === 'object' ? ds.critical : false;

                  return (
                    <div key={i} className={`data-source-item ${critical ? 'critical' : ''}`}>
                      <div className="source-info">
                        <span className="source-name">{name}</span>
                        {type && <span className="source-type">{type}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}
